import { useState, useEffect, useMemo } from 'react'
import Select from "react-select";
import { useAddQueueMutation, useGetQueueDropdownQuery, useUpdateQueueMutation } from "./Api";
import ToastrService from "../../services/toastService";
import Loader from "../loader/loader"
import Swal from "sweetalert2";

const QUEUE_PRIORITY = [
    { label: 'Priority 1', value: 1 },
    { label: 'Priority 2', value: 2 },
    { label: 'Priority 3', value: 3 },
    { label: 'Priority 4', value: 4 },
    { label: 'Priority 5', value: 5 },
];

const QUEUE_STATUSES = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
];

const CHANNEL_OPTIONS = [
    { label: 'Voice', value: 'voice' },
    { label: 'Email', value: 'email' },
    { label: 'Chat', value: 'chat' },
];

const QUEUE_ROUTING = [
    { value: "longest_idle", label: "Longest Idle" },
    // { value: "round_robin", label: "Round Robin" },
    // { value: "skill_based", label: "Skill Based" },
];

const QueueFormDrawer = ({ isOpen, mode, queue, onSave, onClose, }) => {

    const [queueId, setQueueId] = useState("");
    const [queueName, setQueueName] = useState("");
    const [channel, setChannel] = useState(CHANNEL_OPTIONS[0].value);
    const [queuePriority, setQueuePriority] = useState(QUEUE_PRIORITY[0].value);
    const [maximumWaitingTime, setMaximumWaitingTime] = useState("");
    const [selectedRoutingStrategy, setSelectedRoutingStrategy] = useState(QUEUE_ROUTING[0]);
    const [selectedOverflowQueue, setSelectedOverflowQueue] = useState(null);
    const [queueStatus, setQueueStatus] = useState(QUEUE_STATUSES[0].value);

    const [errors, setErrors] = useState({});

    const [addQueue, { isLoading }] = useAddQueueMutation();
    const [updateQueue, { isLoading: updateIsLoading }] = useUpdateQueueMutation();

    const { data: queueDropdownData, refetch: refetchQueueDropdown, } = useGetQueueDropdownQuery(undefined,
        {
            skip: !isOpen,
        });

    const overflowQueueOptions = useMemo(() => {
        const queues = queueDropdownData?.data?.queues || [];

        return queues
            .filter((queueItem) => {
                if (mode === "edit" && queue?.id) {
                    return queueItem.id !== queue.id;
                }
                return true;
            })
            .map((queueItem) => ({
                value: queueItem.id,
                label: queueItem.queue_name,
            }));
    }, [queueDropdownData, mode, queue]);

    useEffect(() => {
        if (!isOpen) return;

        // ADD MODE
        if (mode === "add") {
            resetForm();
            const latestQueue = queueDropdownData?.data?.lastQueueCode;
            setQueueId(getNextQueueCode(latestQueue));
            return;
        }

        // EDIT MODE
        if (mode === "edit" && queue) {

            setQueueId(queue.queue_code || "");
            setQueueName(queue.queue_name || "");
            setChannel(queue.channel || CHANNEL_OPTIONS[0].value);
            setQueuePriority(queue.priority || QUEUE_PRIORITY[0].value);
            setMaximumWaitingTime(queue.maximum_waiting_time ?? "");
            setQueueStatus(queue.status || QUEUE_STATUSES[0].value);

            const routingStrategy = QUEUE_ROUTING.find((item) => item.value === queue.routing_strategy);

            setSelectedRoutingStrategy(routingStrategy || QUEUE_ROUTING[0]);

            const overflowQueue = queueDropdownData?.data?.queues?.find((item) => item.id === queue.overflow_queue_id);

            setSelectedOverflowQueue(
                overflowQueue
                    ? {
                        value: overflowQueue.id,
                        label: overflowQueue.queue_name,
                    }
                    : null
            );

            setErrors({});
        }
    }, [isOpen, mode, queue, queueDropdownData]);


    const getNextQueueCode = (latestQueue) => {
        if (!latestQueue) {
            return "Q-0001";
        }

        const number = parseInt(latestQueue.replace(/\D/g, ""), 10);

        return `Q-${String(number + 1).padStart(4, "0")}`;
    };

    const isFormValid = Boolean(
        queueName.trim() &&
        maximumWaitingTime !== "" && maximumWaitingTime !== null && maximumWaitingTime !== undefined &&
        Number.isInteger(Number(maximumWaitingTime)) &&
        Number(maximumWaitingTime) > 0 &&
        selectedRoutingStrategy
    );

    const clearError = (field) => {
        setErrors((prev) => ({
            ...prev,
            [field]: ""
        }));
    };

    const setFieldError = (field, message) => {
        setErrors((prev) => ({
            ...prev,
            [field]: message
        }));
    };


    const validateForm = () => {
        const newErrors = {};

        if (!queueName.trim()) {
            newErrors.queueName = "Queue Name is required";
        }

        if (maximumWaitingTime === "" || maximumWaitingTime === null || maximumWaitingTime === undefined) {
            newErrors.maximumWaitingTime = "Maximum Waiting Time is required";
        } else if (!Number.isInteger(Number(maximumWaitingTime)) || Number(maximumWaitingTime) <= 0) {
            newErrors.maximumWaitingTime = "Maximum Waiting Time must be greater than 0";
        }

        if (!selectedRoutingStrategy) {
            newErrors.routingStrategy = "Routing Strategy is required";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };


    const handleCreateQueue = async () => {
        try {
            if (!validateForm()) {
                return;
            }
            const payload = {
                queueCode: queueId.trim(),
                queueName: queueName.trim(),
                channel,
                priority: Number(queuePriority),
                maximumWaitingTime: Number(maximumWaitingTime),
                routingStrategy: selectedRoutingStrategy?.value,
                overflowQueue: selectedOverflowQueue?.value || null,
                status: queueStatus,
            };

            if (mode === "add") {
                const response = await addQueue(payload);

                if (response?.data?.success) {
                    ToastrService.success(response?.data?.message);
                    await refetchQueueDropdown();
                    resetForm();
                    onSave();
                } else {
                    ToastrService.error(response?.error?.data?.message || "Failed to create queue");
                }
            } else {
                const updatePayload = {
                    id: queue.id,
                    ...payload,
                };

                const res = await updateQueue(updatePayload).unwrap();

                if (res?.data?.updated === true) {
                    ToastrService.success(res?.message);
                    resetForm();
                    onSave();
                } else {
                    Swal.fire({
                        icon: "info",
                        title: "Cannot Deactivate Queue",
                        text: res?.message || "Queue status cannot be updated.",
                        confirmButtonText: "OK"
                    });
                    // ToastrService.error(res?.error?.data?.message || "Failed to update queue");
                }
            }

        } catch (err) {
            console.log(err, "err");
        }
    };

    const resetForm = () => {
        setQueueName("");
        setChannel(CHANNEL_OPTIONS[0].value);
        setQueuePriority(QUEUE_PRIORITY[0].value);
        setMaximumWaitingTime("");
        setSelectedRoutingStrategy(QUEUE_ROUTING[0]);
        setSelectedOverflowQueue(null);
        setQueueStatus(QUEUE_STATUSES[0].value);
        setErrors({});
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <div>
            {isLoading || updateIsLoading ? <Loader /> :
                <>
                    {isOpen && (
                        <div
                            className="offcanvas-backdrop fade show"
                            onClick={handleClose}
                        ></div>
                    )}
                    <div
                        className={`offcanvas offcanvas-end ${isOpen ? "show" : ""}`}
                        tabIndex="-1"
                        style={{
                            visibility: isOpen ? "visible" : "hidden"
                        }}
                    >
                        <div className="offcanvas-header px-4 py-3">
                            <h3 className="mb-1 drawer-header">
                                {mode === "edit" ? "Edit Queue" : "Create New Queue"}
                            </h3>
                            <button
                                type="button"
                                className="btn-close text-reset"
                                onClick={handleClose}
                            ></button>
                        </div>

                        <div className="offcanvas-body px-4 py-3 ">
                            <form autoComplete="off" noValidate>

                                <div className="mb-3">
                                    <div className="col-md-12">
                                        <label className="form-label fw-semibold">
                                            Queue ID <span className="text-muted">(Auto Generated)</span>
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control"
                                            value={queueId || ""}
                                            readOnly
                                        />
                                    </div>

                                    <div className="col-md-12">
                                        <label className="form-label fw-semibold">
                                            Queue Name <span className="text-danger">*</span>
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g. Queue Name"
                                            value={queueName}
                                            maxLength={50}
                                            onChange={(e) => {
                                                const value = e.target.value;

                                                setQueueName(value);

                                                if (value.trim()) {
                                                    clearError("queueName");
                                                } else {
                                                    setFieldError(
                                                        "queueName",
                                                        "Queue Name is required"
                                                    );
                                                }
                                            }}
                                        />
                                        {errors.queueName && (
                                            <div className="text-danger small mt-1">
                                                {errors.queueName}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mb-2">
                                    <label className="form-label fw-semibold">
                                        Channel
                                    </label>

                                    <select className="form-select"
                                        value={channel}
                                        onChange={(e) => setChannel(e.target.value)}
                                    >
                                        {CHANNEL_OPTIONS.map((type) => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">
                                            Priority
                                        </label>

                                        <select className="form-select"
                                            value={queuePriority}
                                            onChange={(e) => setQueuePriority(e.target.value)}
                                        >
                                            {QUEUE_PRIORITY.map((type) => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">
                                            Maximum Waiting Time <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder="Enter waiting time"
                                                min="0"
                                                step="1"
                                                value={maximumWaitingTime}
                                                onKeyDown={(e) => {
                                                    // Prevent decimal, negative, scientific notation
                                                    if ([".", "e", "E", "+", "-"].includes(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onPaste={(e) => {
                                                    const pastedText = e.clipboardData.getData("text");

                                                    // Allow only positive whole numbers
                                                    if (!/^[1-9]\d{0,4}$/.test(pastedText)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    const value = e.target.value;

                                                    if (value === "") {
                                                        setMaximumWaitingTime("");
                                                        setFieldError(
                                                            "maximumWaitingTime",
                                                            "Maximum Waiting Time is required"
                                                        );
                                                        return;
                                                    }

                                                    if (!/^\d{1,5}$/.test(value)) {
                                                        return;
                                                    }

                                                    const numberValue = Number(value);

                                                    if (Number.isInteger(numberValue) && numberValue > 0) {
                                                        setMaximumWaitingTime(numberValue);
                                                        clearError("maximumWaitingTime");
                                                    }
                                                }}
                                            />
                                            <span className="input-group-text">sec</span>
                                        </div>
                                        {errors.maximumWaitingTime && (
                                            <div className="text-danger small mt-1">
                                                {errors.maximumWaitingTime}
                                            </div>
                                        )}
                                    </div>
                                </div>


                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">
                                            Routing Strategy <span className="text-danger">*</span>
                                        </label>
                                        <Select
                                            options={QUEUE_ROUTING}
                                            value={selectedRoutingStrategy}
                                            placeholder="Select routing strategy"
                                            menuPlacement="auto"
                                            classNamePrefix="overflow-queue-select"
                                            onChange={(selected) => {
                                                setSelectedRoutingStrategy(selected);
                                                if (selected) {
                                                    clearError("routingStrategy");
                                                } else {
                                                    setFieldError(
                                                        "routingStrategy",
                                                        "Routing Strategy is required"
                                                    );
                                                }
                                            }}
                                        />
                                        {errors.routingStrategy && (
                                            <div className="text-danger small mt-1">
                                                {errors.routingStrategy}
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">
                                            Overflow Queue
                                        </label>
                                        <Select
                                            options={overflowQueueOptions}
                                            value={selectedOverflowQueue}
                                            placeholder="Select overflow queue"
                                            menuPlacement="auto"
                                            classNamePrefix="overflow-queue-select"
                                            onChange={(selected) => {
                                                setSelectedOverflowQueue(selected);
                                            }}
                                        />
                                    </div>
                                </div>



                                <div className="col-mb-3">
                                    <label className="form-label fw-semibold">
                                        Status
                                    </label>

                                    <select className="form-select"
                                        value={queueStatus}
                                        onChange={(e) => setQueueStatus(e.target.value)}
                                    >
                                        {QUEUE_STATUSES.map((type) => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>


                                <div className="d-flex justify-content-end gap-2 mt-4">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary px-3 py-2"
                                        onClick={handleClose}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-primary px-3 py-2"
                                        disabled={!isFormValid}
                                        onClick={handleCreateQueue}
                                    >
                                        {mode === "edit" ? "Update Queue" : "Create Queue"}
                                    </button>
                                </div>

                            </form>
                        </div>
                    </div>
                </>
            }
        </div>
    )
}

export default QueueFormDrawer